// ═══════════════════════════════════════════════════════════════
// THEME — Single source of truth for ShopBuilder Pro
// ═══════════════════════════════════════════════════════════════
// Currently using the original navy palette (engineering-tool aesthetic).
// Edit values here to retune the entire app at once.
// Import in any component: import { T, S } from '../theme';

// ─── DESIGN TOKENS ─────────────────────────────────────────────
export const T = {

  // ── Surfaces (backgrounds) ──
  appBg:       '#040b14',        // outer app shell (deep navy)
  barBg:       '#060d18',        // top bar
  panelBg:     '#0a1628',        // dropdowns, modals
  panelRaised: '#07111e',        // sidebars, config panels
  surface:     '#040b14',         // form inputs (matches app bg)
  surfaceHi:   '#162840',         // hover/active state
  canvasBg:    '#03080f',         // CAD canvas background

  // ── Borders & dividers ──
  divider:     '1px solid #162840',
  hairline:    '1px solid #162840',
  hairlineHi:  '1px solid #1e3a54',
  insetHi:     'none',                          // not used in navy theme
  topInset:    'none',
  btnInset:    'none',

  // ── Text colors ──
  text:        '#e8f4ff',         // primary
  textDim:     '#8aa8c5',         // labels, secondary (lightened Phase 3 for contrast; was #4a7a9b)
  textMuted:   '#7290ad',         // captions (lightened Phase 3 for contrast; was #3a6a8a)
  textGhost:   '#2a4a6a',         // very faint placeholder text

  // ── Accent colors ──
  blue:        '#3399ff',         // primary actions (cyan-blue)
  blueDeep:    '#0066cc',         // gradient bottom
  blueSoft:    'rgba(0,80,180,.18)',     // backgrounds for blue-tinted areas

  purple:      '#cc99ff',         // AI brand
  purpleBdr:   '#aa66ff55',
  purpleBg:    'linear-gradient(135deg, rgba(85,0,170,.2), rgba(136,51,255,.15))',
  purpleHover: 'rgba(170,102,255,.1)',

  green:       '#00cc88',         // success
  greenDeep:   '#008c50',
  red:         '#ff6644',         // errors
  redDeep:     '#cc3333',
  yellow:      '#ffe500',         // warnings
  orange:      '#ff9900',         // dimensions

  // ── Gradients (premade combos for buttons) ──
  gradBlue:    'linear-gradient(135deg, #003d80, #0066cc)',
  gradGrey:    'transparent',                    // navy theme uses transparent ghost buttons
  gradGreyHi:  'rgba(255,255,255,0.04)',
  gradGreen:   'rgba(0,140,80,.95)',
  gradRed:     'rgba(180,40,40,.95)',

  // ── Shadows ──
  shadowSm:    '0 1px 2px rgba(0,0,0,0.3)',
  shadowMd:    '0 4px 20px rgba(0,0,0,.5)',
  shadowLg:    '0 8px 40px rgba(0,0,0,.6)',
  shadowXl:    '0 8px 40px rgba(0,0,0,.8)',

  // ── Typography ──
  font:        "'DM Sans','Helvetica Neue',Arial,sans-serif",
  fontMono:    "'JetBrains Mono', 'Menlo', monospace",

  // ── Layout constants ──
  topBarHeight: 48,
  radiusSm:    4,
  radiusMd:    7,
  radiusLg:    8,
  radiusXl:    12,
};

// ─── REUSABLE STYLE OBJECTS ────────────────────────────────────
export const S = {

  // ── Standard ghost button (Save, Load, Cancel, back-nav) ──
  btn: {
    background: 'transparent',
    border: '1px solid #1e3a54',
    borderRadius: 7,
    color: '#6a9fc0',
    fontSize: 11,
    fontWeight: 500,
    padding: '6px 14px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
    display: 'flex', alignItems: 'center', gap: 6,
    transition: 'background .12s',
  },

  // ── Primary blue gradient button (Export, Open Floor Plan) ──
  btnPrimary: {
    background: 'linear-gradient(135deg, #003d80, #0066cc)',
    border: 'none',
    borderRadius: 7,
    color: '#ffffff',
    fontSize: 11,
    fontWeight: 600,
    padding: '6px 14px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
    display: 'flex', alignItems: 'center', gap: 6,
  },

  // ── AI button (purple) ──
  btnAi: {
    background: 'linear-gradient(135deg, rgba(85,0,170,.2), rgba(136,51,255,.15))',
    border: '1px solid #aa66ff55',
    borderRadius: 8,
    color: '#cc99ff',
    fontSize: 11,
    fontWeight: 600,
    padding: '7px 20px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'flex', alignItems: 'center', gap: 7,
    boxShadow: '0 2px 12px rgba(136,51,255,.15)',
  },

  // ── Destructive button ──
  btnDanger: {
    background: 'rgba(180,40,40,.95)',
    border: 'none',
    borderRadius: 7,
    color: '#ffffff',
    fontSize: 11,
    fontWeight: 600,
    padding: '6px 14px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },

  // ── Input field ──
  input: {
    background: '#07111e',
    border: '1px solid #1e3a54',
    borderRadius: 7,
    color: '#e8f4ff',
    padding: '10px 14px',
    fontSize: 13,
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
  },

  // ── Modal panel ──
  modal: {
    background: '#0a1628',
    border: '1px solid #162840',
    borderRadius: 12,
    padding: 28,
    boxShadow: '0 8px 40px rgba(0,0,0,.6)',
    fontFamily: "'DM Sans','Helvetica Neue',Arial,sans-serif",
  },

  // ── Modal backdrop ──
  modalBackdrop: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000,
  },

  // ── Dropdown panel ──
  dropdown: {
    background: '#0a1628',
    border: '1px solid #aa66ff44',
    borderRadius: 10,
    padding: 6,
    boxShadow: '0 8px 32px rgba(0,0,0,.8)',
  },

  // ── Top bar ──
  bar: {
    height: 48,
    background: '#060d18',
    borderBottom: '1px solid #162840',
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    overflow: 'visible',
    position: 'relative',
    zIndex: 100,
    fontFamily: "'DM Sans','Helvetica Neue',Arial,sans-serif",
  },
  cell: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    flexShrink: 0,
    borderRight: '1px solid #162840',
  },
  vSep: {
    width: 1, height: 20,
    background: '#162840',
  },

  // ── Toast notification ──
  toast: {
    position: 'fixed',
    bottom: 24,
    left: '50%',
    transform: 'translateX(-50%)',
    color: '#ffffff',
    padding: '10px 22px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    boxShadow: '0 4px 20px rgba(0,0,0,.5)',
    zIndex: 999,
    pointerEvents: 'none',
    fontFamily: "'DM Sans','Helvetica Neue',Arial,sans-serif",
  },
};
